import React, { useState, useEffect } from "react";
import { fetchFilterOptions, filterJobs } from "../../api/filterApi";
import { getSavedFilter, saveFilter, deleteSavedFilter } from "../../api/savedFilterApi";

import CompanyFilter from "../HomeFilters/CompanyFilter";
import FiltersKeywordGroups from "../HomeFilters/FiltersKeywordGroups";
import JobCard from "../../components/JobCard/JobCard";

import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import "./HomeFilters.css";

function HomeFilters({ employeeId, candidateId, dateFilter, refreshSummary }) {

  /* -------------------------
     Helpers
  -------------------------- */

  const mapArrayToSelect = (arr) => arr?.map(v => ({ label: v, value: v })) || [];
  const mapSecuritySelect = (value) => {
    if (value === null || value === undefined) return null;
    return value ? { label: "Required", value: true } : { label: "Not Required", value: false };
  };

  /* -------------------------
     Local States
  -------------------------- */

  const [filterOptions, setFilterOptions] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [keywordGroups, setKeywordGroups] = useState([[]]); // INTERNAL ONLY

  const [includeCompanies, setIncludeCompanies] = useState([]);
  const [excludeCompanies, setExcludeCompanies] = useState([]);

  const [payloadNames, setPayloadNames] = useState([]);
  const [source, setSource] = useState([]);
  const [sponsorship, setSponsorship] = useState([]);

  const [candidateExperience, setCandidateExperience] = useState("");

  const [includeTitles, setIncludeTitles] = useState([]);
  const [excludeTitles, setExcludeTitles] = useState([]);

  const [jobPortals, setJobPortals] = useState([]);

  const [securityClearance, setSecurityClearance] = useState(null);
  const [groupBy, setGroupBy] = useState("");
  const [totalCount, setTotalCount] = useState(0);



  /* -------------------------
     Derived Options
  -------------------------- */

  const payloadOptions = mapArrayToSelect(filterOptions?.payloadNames);
  const sourceOptions = mapArrayToSelect(filterOptions?.source);
  const sponsorshipOptions = mapArrayToSelect(filterOptions?.sponsorship);
  const titleOptions = mapArrayToSelect(filterOptions?.titles);
  const jobPortalOptions = mapArrayToSelect(filterOptions?.source);

  /* -------------------------
     Load filter options on Date change
  -------------------------- */

  useEffect(() => {
    fetchFilterOptions(dateFilter)
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error("Filter options error:", err));
  }, [dateFilter]);

  /* -------------------------
     Auto-load saved filter
  -------------------------- */

  useEffect(() => {
    if (!employeeId || !candidateId) return;

    getSavedFilter(employeeId, candidateId)
      .then(res => {
        const saved = res.data;
        if (!saved) return;

        console.log("Loaded saved filter:", saved);


        setIncludeCompanies(saved.includeCompanies || []);
        setExcludeCompanies(saved.excludeCompanies || []);

        setPayloadNames(mapArrayToSelect(saved.payloadNames));
        setSource(mapArrayToSelect(saved.source));
        setSponsorship(mapArrayToSelect(saved.sponsorship));

        setIncludeTitles(mapArrayToSelect(saved.includeTitles));
        setExcludeTitles(mapArrayToSelect(saved.excludeTitles));

        setJobPortals(mapArrayToSelect(saved.jobPortals));

        setCandidateExperience(saved.candidateExperience || "");

        setSecurityClearance(mapSecuritySelect(saved.securityClearanceRequired));

        setKeywordGroups(saved.keywordGroups?.length ? saved.keywordGroups : [[]]);
      })
      .catch(() => console.log("No saved filter found."));
  }, [employeeId, candidateId]);

  /* -------------------------
     Apply Filters
  -------------------------- */

  const buildFilterRequest = () => ({
    employeeId,
    candidateId,
    dateFilter,

    includeCompanies: includeCompanies.map(c => c.toLowerCase()),
    excludeCompanies: excludeCompanies.map(c => c.toLowerCase()),

    payloadNames: payloadNames.map(p => p.value),
    source: source.map(s => s.value),
    sponsorship: sponsorship.map(s => s.value),

    includeTitles: includeTitles.map(t => t.value),
    excludeTitles: excludeTitles.map(t => t.value),

    jobPortals: jobPortals.map(j => j.value),

    keywordGroups,
    candidateExperience: candidateExperience ? parseInt(candidateExperience) : null,

    securityClearanceRequired: securityClearance?.value ?? null,
    groupBy: groupBy || null,

    page: 0,
    pageSize: 100
  });

  const handleApplyFilters = () => {
    const req = buildFilterRequest();

    setLoading(true);

    filterJobs(req)
      .then(res => {
        setResults(res.data.content || []);
        setTotalCount(res.data.totalElements ?? (res.data.content?.length || 0));

        if (refreshSummary) refreshSummary();

      })
      .catch(err => console.error("Filter API error:", err))
      .finally(() => setLoading(false));
  };

  /* -------------------------
     Save Filter
  -------------------------- */

  const handleSaveFilter = () => {
    const req = buildFilterRequest();

    saveFilter(employeeId, candidateId, req)
      .then(() => alert("Filters saved!"))
      .catch(() => alert("Error saving filters."));
  };

  /* -------------------------
     Reset Filter
  -------------------------- */

  const handleResetFilters = () => {
    deleteSavedFilter(employeeId, candidateId).catch(() => { });

    setKeywordGroups([[]]);

    setIncludeCompanies([]);
    setExcludeCompanies([]);

    setPayloadNames([]);
    setSource([]);
    setSponsorship([]);

    setIncludeTitles([]);
    setExcludeTitles([]);

    setJobPortals([]);

    setCandidateExperience("");
    setSecurityClearance(null);

    setResults([]);

    fetchFilterOptions(dateFilter).then(res => setFilterOptions(res.data));
  };

  /* -------------------------
     Clear Results only
  -------------------------- */

  const handleClearResults = () => {
    setResults([]);
    setTotalCount(0);
  };

  /* -------------------------
     Render
  -------------------------- */

  return (
    <div className="hf-container">

      <div className="hf-filters-wrapper">


        <CompanyFilter
          filterOptions={filterOptions}
          includeCompanies={includeCompanies}
          setIncludeCompanies={setIncludeCompanies}
          excludeCompanies={excludeCompanies}
          setExcludeCompanies={setExcludeCompanies}
        />

        {/* Payload */}
        <div className="hf-filter-item">
          <label>Payload</label>
          <Select
            options={payloadOptions}
            value={payloadNames}
            onChange={setPayloadNames}
            isMulti
            classNamePrefix="hf"
          />
        </div>

        {/* Source */}
        <div className="hf-filter-item">
          <label>Source</label>
          <Select
            options={sourceOptions}
            value={source}
            onChange={setSource}
            isMulti
            classNamePrefix="hf"
          />
        </div>

        {/* Sponsorship */}
        <div className="hf-filter-item">
          <label>Sponsorship</label>
          <Select
            options={sponsorshipOptions}
            value={sponsorship}
            onChange={setSponsorship}
            isMulti
            classNamePrefix="hf"
          />
        </div>

        {/* Security Clearance */}
        <div className="hf-filter-item">
          <label>Security Clearance</label>
          <Select
            options={[
              { label: "Required", value: true },
              { label: "Not Required", value: false }
            ]}
            value={securityClearance}
            onChange={setSecurityClearance}
            isClearable
            classNamePrefix="hf"
          />
        </div>

        {/* Experience */}
        <div className="hf-filter-item">
          <label>Experience (Years)</label>
          <input
            type="number"
            min="0"
            placeholder="e.g. 4"
            value={candidateExperience}
            onChange={(e) => setCandidateExperience(e.target.value)}
          />
        </div>

        {/* Include Titles */}
        <div className="hf-filter-item">
          <label>Include Titles</label>
          <CreatableSelect
            isMulti
            value={includeTitles}
            onChange={setIncludeTitles}
            options={titleOptions}
            classNamePrefix="hf"
          />
        </div>

        {/* Exclude Titles */}
        <div className="hf-filter-item">
          <label>Exclude Titles</label>
          <CreatableSelect
            isMulti
            value={excludeTitles}
            onChange={setExcludeTitles}
            options={titleOptions}
            classNamePrefix="hf"
          />
        </div>

        {/* Job Portals */}
        <div className="hf-filter-item">
          <label>Job Portals</label>
          <CreatableSelect
            isMulti
            value={jobPortals}
            onChange={setJobPortals}
            options={jobPortalOptions}
            classNamePrefix="hf"
          />
        </div>
      </div>

      {/* Keyword Groups (big section) */}
      <div className="hf-keywords-wrapper">
        <FiltersKeywordGroups
          keywordGroups={keywordGroups}
          setKeywordGroups={setKeywordGroups}
        />
      </div>

      <div className="hf-groupby">
        <label>Group By</label>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="hf-select"
        >
          <option value="">No Grouping</option>
          <option value="company">Company</option>
          <option value="source">Source</option>
          <option value="title">Title</option>
        </select>
      </div>

      {/* Action Buttons */}
      <div className="hf-action-buttons">
        <button className="hf-btn-apply" onClick={handleApplyFilters}>Apply</button>
        <button className="hf-btn" onClick={handleClearResults}>Clear</button>
        <button className="hf-btn-save" onClick={handleSaveFilter}>Save</button>
        <button className="hf-btn" onClick={handleResetFilters}>Delete Saved Filter</button>

      </div>

      {/* RESULTS */}
      <div className="hf-results">
        <h3>
          {loading ? "Loading..." : `Results (${totalCount || 0})`}
        </h3>

        {results.length === 0 && !loading && (
          <p className="hf-no-results">No jobs found.</p>
        )}

        {results.map((item) => (
          <JobCard key={item.job.id} job={item.job} status={item.status} employeeId={employeeId}
            candidateId={candidateId} />
        ))}
      </div>

    </div>
  );
}

export default HomeFilters;
